import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useUser } from '@/lib/auth/react';
import { useQueryClient } from '@tanstack/react-query';
import { BottomSheet } from '@/components/ui';
import { useAuthModal, useOpenSignup, useCloseAuthModal } from '@/store/authModalStore';
import { useSignupInProgress } from '@/store/signupProgressStore';
import { useEmailVerificationState, emailVerificationStateKey } from '@/lib/auth/useEmailVerificationState';
import { needsEmailVerification } from '@/lib/onboarding';
import { useSignupTrack } from '@/analytics';
import { SheetBackButton } from './auth-form-ui';

const TEST_ID_PREFIX = 'auth-signup-email-verified';

/** How often to (re)arm the Firebase watcher — also the effective poll cadence. */
const WATCH_REARM_MS = 3000;

const noop = () => {};

/**
 * Email-verification page.
 *
 * Mounted once at the app root, shown when the auth modal is in `verifyEmail`
 * mode and the in-progress signup still has an unverified email. Living at the
 * app level (not as a step inside the signup bottom-sheet) gives it a single
 * stable mount, which is what the Firebase watcher needs — the old in-sheet step
 * kept getting torn down, so the watcher never reliably ran. Visually it is the
 * same signup sheet the user already knows (logo · stepper · "Check Your Email!"
 * card).
 *
 * Modal-driven, not self-forcing: the (in-memory) modal mode gates it, so a page
 * refresh or an intentional dismiss leaves the funnel closed; re-entering signup
 * routes back here while the email is still unverified (ARC-145 "page refresh").
 *
 * Two inputs, two owners:
 *  - **Send state** (was an email sent, resend cooldown) comes from the backend
 *    (Redis), fetched on mount — so a refresh neither re-sends nor resets the timer.
 *  - **Verified?** is watched from Firebase. On the click the auth client mirrors
 *    it and flips the user to verified; this page then advances the funnel to
 *    the terms step itself (no refresh needed).
 *
 * The watcher is re-armed on an interval and on tab focus. Right after a refresh
 * Firebase may not have restored `currentUser` yet and the watcher gives up
 * immediately; re-arming guarantees it catches once the session is ready. The
 * focus re-arm matters for the common verify path — the user switches to their
 * mail client / the verify tab and comes back — where background-tab timer
 * throttling would otherwise delay the advance and make it look stuck.
 */
export function EmailVerificationPage() {
  const { client } = useAuth();
  const user = useUser();
  const modal = useAuthModal();
  const openSignup = useOpenSignup();
  const closeAuthModal = useCloseAuthModal();
  const signupInProgress = useSignupInProgress();
  const queryClient = useQueryClient();
  const track = useSignupTrack();

  const active = modal === 'verifyEmail' && signupInProgress && needsEmailVerification(user);

  // The verification completed while this step owns the modal — advance the
  // funnel to the terms step in place (the signup sheet resumes at `terms` for
  // a verified session). This replaces the old "refresh to see the T&S" gap.
  useEffect(() => {
    if (modal === 'verifyEmail' && signupInProgress && user && !needsEmailVerification(user)) openSignup();
  }, [modal, signupInProgress, user, openSignup]);

  const { data: state } = useEmailVerificationState(active);

  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  // Number of manual resends this session — rides on `email_verification_resent`.
  const resendCountRef = useRef(0);

  // `email_verification_sent`: the Verify-your-Email screen rendered (and the
  // backend send runs as part of its lifecycle). Fire once per time the screen
  // becomes active; reset on close so a later re-entry fires again.
  const sentTrackedRef = useRef(false);
  useEffect(() => {
    if (!active) {
      sentTrackedRef.current = false;
      return;
    }
    if (sentTrackedRef.current) return;
    sentTrackedRef.current = true;
    track('email_verification_sent', { auth_method: 'email' });
  }, [active, track]);

  // `email_verified`: the watched verification completed — the page was showing
  // and the user is now verified, so the funnel hands off to the terms step.
  // Bare milestone (globals only). Guarded to fire once.
  const wasActiveRef = useRef(false);
  const verifiedTrackedRef = useRef(false);
  useEffect(() => {
    if (active) wasActiveRef.current = true;
    if (wasActiveRef.current && !verifiedTrackedRef.current && signupInProgress && !!user?.email && user.isEmailVerified) {
      verifiedTrackedRef.current = true;
      track('email_verified', {});
    }
  }, [active, signupInProgress, user, track]);

  // Send (or resend) via the backend, which is idempotent within its cooldown.
  const send = useCallback(async () => {
    setError(null);
    setIsSending(true);
    try {
      await client.sendEmailVerification();
      await queryClient.invalidateQueries({ queryKey: emailVerificationStateKey() });
    } catch {
      setError('We could not send the verification email. Please try again.');
    } finally {
      setIsSending(false);
    }
  }, [client, queryClient]);

  // First send: once, only if the server says none is outstanding.
  const sentRef = useRef(false);
  useEffect(() => {
    if (!active) {
      sentRef.current = false;
      return;
    }
    if (sentRef.current || !state) return;
    sentRef.current = true;
    if (!state.emailSent) void send();
  }, [active, state, send]);

  // Watch Firebase for the verification click. Re-arm so it survives the
  // post-refresh window where Firebase hasn't restored `currentUser` yet, and
  // re-arm immediately on tab focus — background tabs throttle timers, so the
  // "verified in the email tab, switched back" path would otherwise sit on a
  // stale watcher for up to a minute.
  useEffect(() => {
    if (!active) return;
    let stop = noop;
    const arm = () => {
      stop();
      stop = client.watchEmailVerification(
        () => void queryClient.invalidateQueries({ queryKey: emailVerificationStateKey() }),
        { onError: noop },
      );
    };
    arm();
    const id = setInterval(arm, WATCH_REARM_MS);
    const onFocus = () => {
      if (!document.hidden) arm();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      stop();
    };
  }, [active, client, queryClient]);

  // Seed the resend countdown from the server (reseed only when a new email is
  // sent, i.e. the marker changes), then tick it down locally.
  const seededFromRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!state) return;
    if (seededFromRef.current !== state.lastSentAt) {
      seededFromRef.current = state.lastSentAt;
      setCooldown(state.cooldownSecondsLeft);
    }
  }, [state]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const canResend = cooldown === 0 && !isSending;

  // Dismissable (ARC-145 "page refresh"): backdrop/Escape closes the funnel like
  // any other step. The persisted signup-progress flag survives, so re-entering
  // signup resumes right back here.
  function handleDismiss() {
    track('onboarding_exited', { exited_at_step: 'profile' });
    closeAuthModal();
  }

  return (
    <BottomSheet open={active} onClose={handleDismiss}>
      <div className="relative flex flex-col gap-5 w-full max-w-[377px] mx-auto min-h-[590px]">
        <SheetBackButton testId={`${TEST_ID_PREFIX}-back-button`} onClick={handleDismiss} />

        <div data-testid={TEST_ID_PREFIX} className="flex-1 flex flex-col items-center pt-10">
          <h2 className="text-2xl font-bold text-ink text-center">Verify your Email</h2>

          <p className="mt-4 text-lg text-ink text-center max-w-[340px]">
            We&rsquo;ve sent a confirmation email to
            <br />
            <span data-testid={`${TEST_ID_PREFIX}-email`} className="font-bold">
              {user?.email}
            </span>
          </p>

          {error && (
            <p
              data-testid={`${TEST_ID_PREFIX}-error`}
              className="mt-6 text-sm text-danger-alt text-center px-2"
              role="alert"
            >
              {error}
            </p>
          )}

          <div className="mt-auto flex flex-col items-center gap-3 pb-4">
            <p className="text-sm text-ink text-center max-w-[300px]">
              If you don&rsquo;t see the email, please check your spam folder.
            </p>
            <p className="text-sm text-ink text-center">
              Did not get an email?{' '}
              <button
                data-testid={`${TEST_ID_PREFIX}-resend-button`}
                type="button"
                onClick={() => {
                  if (!canResend) return;
                  resendCountRef.current += 1;
                  track('email_verification_resent', { auth_method: 'email', resend_count: resendCountRef.current });
                  void send();
                }}
                disabled={!canResend}
                className="font-semibold text-success underline disabled:opacity-50 disabled:no-underline"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : isSending ? 'Sending…' : 'Resend email'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}
